import { useState, useCallback, useRef } from 'react'
import {
  clusterScenesWithAudio,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage audio clustering with scene structure processing
 * Handles clustering initiation and status polling
 */
export const useAudioClustering = ({
  formData,
  setFormData,
  formatSceneObj,
  videoId
} = {}) => {
  const [isClusteringAudio, setIsClusteringAudio] = useState(false)
  const [audioClusteringProgress, setAudioClusteringProgress] = useState(0)
  const [audioClusteringStatus, setAudioClusteringStatus] = useState('')

  /**
   * Format shot list for clustering API
   */
  const formatShotListForClustering = useCallback(shotList => {
    const sceneStructure = {}

    shotList.forEach((scene, index) => {
      const sceneKey = `scene ${index + 1}`
      const shotsObj = {}

      scene.shots.forEach((shot, sIndex) => {
        const shotKey = `shot ${sIndex + 1}`
        shotsObj[shotKey] = {
          ...shot,
          title: shot.title || '',
          shot_direction: shot.shot_direction || '',
          voiceover_text: shot.voiceover_text || '',
          initial_image_prompt: shot.initial_image_prompt || '',
          video_prompt: shot.video_prompt || '',
          scenery: shot.scenery || '',
          location: shot.location || '',
          lighting: shot.lighting || '',
          dialogue: shot.dialogue || {},
          video_duration: null
        }
      })

      sceneStructure[sceneKey] = {
        ...scene,
        scene_location: scene.scene_location || '',
        aggregated_voiceover: scene.aggregated_voiceover || '',
        audio_link: null,
        audio_duration: null,
        audio_duration_estimated: true,
        estimated_audio_duration: scene.estimated_audio_duration || null,
        shots: shotsObj
      }
    })

    return sceneStructure
  }, [])

  /*
   * Poll audio clustering task status
   */
  const pollAudioClustering = useCallback(
    async (taskId, originalShotsData) => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          const isSuccess =
            response.data &&
            (response.data.status === 'success' ||
              (response.data.progress === 100 && response.data.output))

          if (isSuccess && response.data && response.data.output) {
            try {
              const sceneData = response.data.output

              setFormData(prev => {
                const currentShotList = [...(prev.shotList || [])]

                // Merge response data into existing shot list
                const updatedShotList = currentShotList.map((scene, index) => {
                  let responseScene = null

                  if (Array.isArray(sceneData)) {
                    responseScene = sceneData[index]
                  } else {
                    const sceneKey = `scene ${index + 1}`
                    responseScene =
                      sceneData[sceneKey] || Object.values(sceneData)[index]
                  }

                  if (!responseScene) return scene

                  // Create updated scene object
                  const updatedScene = {
                    ...scene,
                    audio_link: responseScene.audio_link || scene.audio_link,
                    audio_duration:
                      responseScene.audio_duration || scene.audio_duration,
                    scene_location:
                      responseScene.scene_location || scene.scene_location,
                    aggregated_voiceover:
                      responseScene.aggregated_voiceover ||
                      scene.aggregated_voiceover,
                    audio_duration_estimated: false // We now have real duration
                  }

                  // Update shots with video_duration from response
                  if (responseScene.shots) {
                    updatedScene.shots = scene.shots.map((shot, sIndex) => {
                      let responseShot = null

                      if (Array.isArray(responseScene.shots)) {
                        responseShot = responseScene.shots[sIndex]
                      } else {
                        const shotKey = `shot ${sIndex + 1}`
                        responseShot =
                          responseScene.shots[shotKey] ||
                          Object.values(responseScene.shots)[sIndex]
                      }

                      if (!responseShot) return shot

                      return {
                        ...shot,
                        video_duration:
                          responseShot.video_duration || shot.video_duration
                      }
                    })
                  }

                  return updatedScene
                })

                return {
                  ...prev,
                  shotList: updatedShotList,
                  shotListWithAudio: updatedShotList, // Also update audio version
                  audio: {
                    ...prev.audio,
                    status: 'completed'
                  }
                }
              })

              setIsClusteringAudio(false)
              setAudioClusteringStatus('completed')
              break
            } catch (err) {
              console.error('Error processing audio clustering output:', err)
              setIsClusteringAudio(false)
              setAudioClusteringStatus('failed')
              break
            }
          } else if (response.data && response.data.status === 'failed') {
            console.error('Audio clustering task failed:', response.data)

            // Fallback to original shots data if clustering fails
            if (originalShotsData && formatSceneObj) {
              try {
                const fallbackScenes = formatSceneObj(originalShotsData)
                setFormData(prev => ({
                  ...prev,
                  shotList: fallbackScenes,
                  audio: {
                    ...prev.audio,
                    status: 'failed'
                  }
                }))
              } catch (fallbackErr) {
                console.error('Fallback formatting failed', fallbackErr)
              }
            } else {
              setFormData(prev => ({
                ...prev,
                audio: {
                  ...prev.audio,
                  status: 'failed'
                }
              }))
            }

            setIsClusteringAudio(false)
            setAudioClusteringStatus('failed')
            break
          } else {
            setAudioClusteringProgress(
              response.data && response.data.progress
                ? response.data.progress
                : audioClusteringProgress
            )
            setAudioClusteringStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          console.error('Error polling audio clustering:', error)
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData, audioClusteringProgress, formatSceneObj]
  )

  const isInitiatingRef = useRef(false)
  const lastPayloadRef = useRef(null)

  const initiateAudioClustering = useCallback(
    async (shotList, fullShotResponse, options = {}) => {
      // Check if we are already initiating a request
      if (isInitiatingRef.current) {
        console.log(
          'Audio clustering initiation already in progress, skipping...'
        )
        return
      }

      // Check if we have a selected voice first
      // Priority: passed voiceId > options.voiceId > formData.selectedVoice
      const selectedVoice =
        options.voiceId || options.voice_id || formData?.selectedVoice

      if (!selectedVoice) {
        console.warn('No voice selected, skipping audio clustering')
        return
      }

      const voiceIdToUse =
        typeof selectedVoice === 'string'
          ? selectedVoice
          : selectedVoice.id || selectedVoice.voice_id

      // Create payload to check for redundancy
      const currentPayload = JSON.stringify({
        scene_structure: shotList,
        voice_id: voiceIdToUse,
        video_id: videoId
      })

      // Skip if payload is identical to last one AND we are currently clustering
      if (currentPayload === lastPayloadRef.current && isClusteringAudio) {
        console.log(
          'Duplicate audio clustering request with same data, skipping...'
        )
        return
      }

      isInitiatingRef.current = true
      setIsClusteringAudio(true)
      setAudioClusteringProgress(0)
      setAudioClusteringStatus('Starting...')

      try {
        const payload = {
          scene_structure: shotList,
          voice_id: voiceIdToUse,
          video_id: videoId
        }

        lastPayloadRef.current = currentPayload
        const res = await clusterScenesWithAudio(payload)
        const taskId = res && res.data && res.data.task_id

        if (taskId) {
          setAudioClusteringStatus('Processing...')
          pollAudioClustering(taskId, fullShotResponse)
        } else {
          setIsClusteringAudio(false)
          setAudioClusteringStatus('Failed to start')
          lastPayloadRef.current = null // Reset on failure
        }
      } catch (error) {
        console.error('Audio clustering failed:', error)
        setIsClusteringAudio(false)
        setAudioClusteringStatus('Error')
        lastPayloadRef.current = null // Reset on failure
      } finally {
        isInitiatingRef.current = false
      }
    },
    [formData?.selectedVoice, videoId, pollAudioClustering, isClusteringAudio]
  )

  /**
   * Resume polling for an existing audio clustering task (used after page refresh)
   */
  const resumeAudioPolling = useCallback(
    async taskId => {
      if (!taskId || isClusteringAudio) return

      setIsClusteringAudio(true)
      setAudioClusteringStatus('Resuming audio generation...')
      await pollAudioClustering(taskId)
    },
    [isClusteringAudio, pollAudioClustering]
  )

  /**
   * Reset audio clustering state
   */
  const resetAudioClustering = useCallback(() => {
    setIsClusteringAudio(false)
    setAudioClusteringProgress(0)
    setAudioClusteringStatus('')
  }, [])

  return {
    // State
    isClusteringAudio,
    audioClusteringProgress,
    audioClusteringStatus,

    // Actions
    initiateAudioClustering,
    formatShotListForClustering,
    resumeAudioPolling,
    resetAudioClustering
  }
}

export default useAudioClustering
